import { FocusEvent, KeyboardEvent, useState } from 'react';
import {
  hasModifier,
  nextRadioIndex,
  radioIndexOf,
  radiosOf,
  resolveTabStopIndex,
  type RadioItem,
} from './radioGroupNavigation';

type RovingRadioGroupInput = {
  /** The radios of the group in the order they appear on the page. */
  items: readonly RadioItem[];
  selectedId: string | null;
  /** Called with the position of a radio when it takes focus, however it got it. */
  onRadioFocus?: (index: number) => void;
};

/**
 * Keyboard behaviour for a group of radios: one tab stop (the focused radio, else the chosen one,
 * else the first available one), and the arrow keys move focus along the group, wrapping at the
 * ends, without the browser's own scroll. The arrow keys never choose. Spread `groupHandlers` on the element with
 * `role="radiogroup"` and give each radio `tabIndexOf(index)`.
 */
export function useRovingRadioGroup({
  items,
  selectedId,
  onRadioFocus,
}: Readonly<RovingRadioGroupInput>) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const tabStopIndex = resolveTabStopIndex({ items, focusedIndex, selectedId });

  const groupHandlers = {
    onFocus: (event: FocusEvent<HTMLElement>) => {
      const index = radioIndexOf(radiosOf(event.currentTarget), event.target);
      setFocusedIndex(index);
      onRadioFocus?.(index);
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setFocusedIndex(null);
      }
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (hasModifier(event)) {
        return;
      }
      const radios = radiosOf(event.currentTarget);
      const next = nextRadioIndex(event.key, radioIndexOf(radios, event.target), radios.length);
      if (next === null) {
        return;
      }
      event.preventDefault();
      // The browser would scroll the radio into view in its own way, which can leave it cut off.
      // The group that owns the scrolling box does that itself, from `onRadioFocus`.
      radios[next].focus({ preventScroll: true });
    },
  };

  const tabIndexOf = (index: number): 0 | -1 => (index === tabStopIndex ? 0 : -1);

  return { groupHandlers, tabIndexOf };
}
